<!-- src/App.vue -->
<template>
  <div id="app">
    <div class="background-layer" aria-hidden="true"></div>
    <header class="site-header">
      <router-link to="/" class="brand-link">
        <span class="brand-mark"></span>
        Henium
      </router-link>
      <nav class="site-nav">
        <router-link to="/">Home</router-link>
        <div class="nav-dropdown">
          <router-link to="/boozelog" class="nav-parent">Boozelog</router-link>
          <div class="nav-menu">
            <router-link to="/boozelog/support">Support</router-link>
            <router-link to="/boozelog/privacy">Privacy</router-link>
          </div>
        </div>
        <router-link to="/decryptor">Decryption Utility</router-link>
        <router-link to="/contact">Contact</router-link>
      </nav>
    </header>

    <main class="page-shell">
      <router-view />
    </main>
    <footer class="site-footer">
      <span>Boozelog for mindful pacing and reflection.</span>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'App',
});
</script>

<style>
:root {
  color-scheme: light;
  --ink: #0c2433;
  --muted-ink: #355061;
  --mist: rgba(255, 255, 255, 0.65);
  --border: rgba(255, 255, 255, 0.45);
  --accent: #ffb37a;
  --accent-deep: #ff8f6a;
  --teal: #2ea7ad;
  --blue: #4c8fe6;
  --shadow: 0 18px 40px rgba(10, 35, 52, 0.18);
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: "Gill Sans", "Trebuchet MS", "Verdana", sans-serif;
  color: var(--ink);
  background: linear-gradient(140deg, #e8fbff 0%, #d9f0ff 35%, #fff0e3 100%);
  min-height: 100vh;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.background-layer {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.background-layer::before,
.background-layer::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(46, 167, 173, 0.35), transparent 60%);
  filter: blur(2px);
  animation: float 18s ease-in-out infinite;
}

.background-layer::before {
  width: 420px;
  height: 420px;
  top: -80px;
  left: -60px;
}

.background-layer::after {
  width: 520px;
  height: 520px;
  bottom: -140px;
  right: -120px;
  animation-delay: -6s;
  background: radial-gradient(circle, rgba(255, 179, 122, 0.35), transparent 60%);
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 56px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(18px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Palatino", "Book Antiqua", "Georgia", serif;
  font-size: 1.5rem;
  line-height: 1;
  letter-spacing: 0.02em;
  text-decoration: none;
  color: var(--ink);
}

.brand-mark {
  width: 34px;
  height: 34px;
  border-radius: 14px;
  background: radial-gradient(circle at top, rgba(255, 255, 255, 0.8), rgba(76, 143, 230, 0.8));
  box-shadow: inset 0 1px 6px rgba(255, 255, 255, 0.6);
  position: relative;
}

.brand-mark::after {
  content: "";
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  top: 7px;
  right: 8px;
}

.site-nav {
  display: flex;
  gap: 28px;
  font-size: 1rem;
  align-items: center;
}

.site-nav a {
  color: var(--muted-ink);
  text-decoration: none;
  padding-bottom: 4px;
  line-height: 1;
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.site-nav a.router-link-active {
  color: var(--ink);
  border-color: var(--teal);
}

.site-nav a:hover {
  color: var(--ink);
}

.nav-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nav-dropdown::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 12px;
}

.nav-parent::after {
  content: "";
  display: inline-block;
  margin-left: 8px;
  border-width: 4px 4px 0 4px;
  border-style: solid;
  border-color: currentColor transparent transparent transparent;
  transform: translateY(-1px);
}

.nav-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 180px;
  padding: 10px;
  display: grid;
  gap: 8px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  box-shadow: 0 16px 30px rgba(12, 36, 51, 0.18);
  opacity: 0;
  pointer-events: none;
  transform: translateY(6px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  z-index: 3;
}

.nav-menu::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  height: 12px;
}

.nav-menu a {
  padding: 6px 10px;
  border-radius: 10px;
}

.nav-menu a:hover {
  background: rgba(46, 167, 173, 0.12);
}

.nav-dropdown:hover .nav-menu,
.nav-dropdown:focus-within .nav-menu {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.page-shell {
  position: relative;
  z-index: 1;
  padding: 40px 56px 80px;
  flex: 1;
}

.site-footer {
  z-index: 1;
  padding: 24px 56px 40px;
  color: var(--muted-ink);
  font-size: 0.95rem;
}

.page {
  animation: fadeUp 0.8s ease both;
}

.glass-card {
  background: var(--mist);
  border: 1px solid var(--border);
  border-radius: 24px;
  box-shadow: var(--shadow);
  backdrop-filter: blur(18px);
}

.button-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border-radius: 999px;
  background: linear-gradient(120deg, var(--teal), var(--blue));
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 14px 30px rgba(46, 167, 173, 0.35);
}

.button-primary:hover {
  transform: translateY(-1px);
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  font-size: 0.75rem;
  background: rgba(12, 36, 51, 0.2);
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(24px);
  }
  100% {
    transform: translateY(0px);
  }
}

@media (max-width: 900px) {
  .site-header {
    padding: 20px 24px;
  }

  .page-shell {
    padding: 32px 24px 64px;
  }

  .site-nav {
    gap: 18px;
  }
}

@media (max-width: 720px) {
  .site-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .site-nav {
    flex-wrap: wrap;
  }

  .nav-dropdown {
    width: 100%;
  }

  .nav-menu {
    position: static;
    opacity: 1;
    pointer-events: auto;
    transform: none;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }
}
</style>
